import { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { IoCloseOutline } from 'react-icons/io5';
//
import Checkbox from '../../../components/ui/Checkbox';
import RecentProductCard from '../../../components/RecentProductCard';
import { Subtitle } from '../../../components/ui';
import { parseHashParams } from '../../../utils/hashRouter';
import {
  getCodeList,
  getFilterMenuList,
} from '../../../api/services/systemService';

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
  background: ${(props) => props.theme.colors.white};
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
`;

const FilterGroup = styled.div`
  margin-bottom: 1rem;
  border: 2px solid ${(props) => props.theme.colors.subBackground};
  border-radius: 5px;
`;

const SelectedFilterGroup = styled.div`
  display: flex;
  width: 100%;
  background: ${(props) => props.theme.colors.subBackground};
  align-items: center;
  padding: 12px 10px;
  border-radius: 10px;
  gap: 10px;
  flex-wrap: wrap;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    padding: 10px;
    margin-bottom: 10px;
  }
`;

const SelectedFilterItem = styled.div`
  display: flex;
  background: ${(props) => props.theme.colors.white};
  justify-content: center;
  align-items: center;
  padding: 5px 10px;
  border-radius: 10px;
  font-size: 13px;
  gap: 5px;
`;

const CloseButton = styled.button`
  padding: 0;
  display: flex;
  background: none;
  border: none;
  cursor: pointer;
`;

const ClearButton = styled.button`
  display: flex;
  align-self: flex-end;
  padding: 0;
  background: none;
  border: none;
  margin-right: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  width: fit-content;
`;

const GroupHeader = styled.div`
  display: flex;
  width: 100%;
  background: ${(props) => props.theme.colors.subBackground};
  justify-content: space-between;
  align-items: center;
  padding: 12px 10px;
  cursor: pointer;
  user-select: none;
  box-sizing: border-box;

  @media (max-width: 768px) {
    width: 100%;
    padding: 10px;
    margin-bottom: 5px;
  }
`;

const DropdownArrow = styled.span`
  border: solid ${(props) => props.theme.colors.primaryText};
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 3px;
  transform: rotate(${(props) => (props.isOpen ? '-135deg' : '45deg')});
  transition: transform 0.2s ease;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px;
  margin: 16px 0 8px 0;
  border: 1px solid ${(props) => props.theme.colors.subBackground};
  border-radius: 5px;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #888;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
  }

  @media (max-width: 768px) {
    width: 100%;
    margin: 10px 0;
  }
`;

const FilterItem = styled.div`
  margin: 8px 0;
  padding: 0 10px;
  width: 100%;
  box-sizing: border-box;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #444;
  cursor: pointer;
  transition: color 0.2s;
  padding: 4px 8px;
  width: 100%;
  box-sizing: border-box;
  border-radius: 4px;

  &:hover {
    color: #000;
    background: ${(props) => props.theme.colors.subBackground};
  }
`;

const NoItemsMessage = styled.div`
  padding: 10px;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

export default function SearchOptionView({
  category,
  categoryName,
  categoryId,
  selectedFilterList,
  onSelectedChange,
}) {
  const userInfo = useSelector((state) => state.userInfo);
  const theme = useTheme();
  const recentViewList = userInfo.recentViewItemsList || [];

  const [categoryList, setCategoryList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownState, setDropdownState] = useState({});
  const [isReady, setIsReady] = useState(false);
  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);
  const menuRequestRef = useRef(0);

  // Availability filter options
  const availabilityFilters = [
    { id: 'availability_in_stock', title: 'In Stock', name: 'availability' },
    {
      id: 'availability_out_of_stock',
      title: 'Out of Stock',
      name: 'availability',
    },
  ];

  useEffect(() => {
    // Check if there are filters in the URL before clearing
    const { params } = parseHashParams();
    const filterParam = params.get('filter');

    // Only clear filters if there's no filter parameter in the URL
    if (!filterParam) {
      onSelectedChange([]);
    }

    setCategoryList([]);
    setHasRestoredFilters(false); // Reset restoration flag when category changes
    getDbMenuList();
  }, [category, categoryName]);

  // Restore filters from URL once categoryList is loaded
  useEffect(() => {
    if (isReady && categoryList.length > 0 && !hasRestoredFilters) {
      restoreFiltersFromURL();
      setHasRestoredFilters(true);
    }
  }, [isReady, categoryList, hasRestoredFilters]);

  const getDbMenuList = async () => {
    const requestId = ++menuRequestRef.current;
    // Extract the display name from the URL key if categoryName is not available
    const getCategoryDisplayName = () => {
      if (categoryName) {
        return categoryName;
      }

      // Fallback: Extract last part of URL key and convert to display format
      // This is a basic fallback and won't perfectly reconstruct names with special chars
      if (category) {
        const parts = category.split('-');
        const lastPart = parts[parts.length - 1];
        // Capitalize first letter
        const fallbackName =
          lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
        return fallbackName;
      }

      return category;
    };

    const cateNm = getCategoryDisplayName();
    const urlKey = category;

    let result = await getFilterMenuList({
      idWebCate: categoryId,
      cateNm,
      urlKey,
    });

    if (requestId !== menuRequestRef.current) {
      return;
    }

    const categoryList = [];
    const dropDownMap = {};

    if (result.filterList.length > 0 && result.filterList[0].filterGroup) {
      const filterGroupList = result.filterList[0].filterGroup
        .split(',')
        .filter((item) => item.trim() !== '')
        .map((item) => item.trim());

      // Fetch codes with specific filter group IDs
      const resolvedCateNm = result.filterList[0].cateNm || cateNm;
      const resolvedUrlKey = result.filterList[0].urlKey || urlKey;
      const codeResult = await getCodeList(
        '88',
        filterGroupList,
        resolvedCateNm,
        resolvedUrlKey,
      );

      if (requestId !== menuRequestRef.current) {
        return;
      }

      if (codeResult.codeList && codeResult.codeList.length > 0) {
        for (const code of codeResult.codeList) {
          const filterList = [];

          // Parse filter IDs and titles
          const filterIdList = code.item1
            .split(',')
            .filter((item) => item.trim() !== '');
          const filterNameList = code.codeNm;
          const filterTitleList = code.item2
            .split(',')
            .filter((item) => item.trim() !== '');

          // Build filter items
          for (let i = 0; i < filterIdList.length; i++) {
            const filterItem = {
              id: filterIdList[i],
              name: filterNameList || '',
              title: filterTitleList[i] || '',
            };
            filterList.push(filterItem);
          }

          // Create category item
          const categoryItem = {
            id: String(code.idCode),
            title: String(code.idCode),
            codeNm: code.codeNm || String(code.idCode),
            filterList,
          };
          categoryList.push(categoryItem);
          dropDownMap[String(code.idCode)] = true;
        }
      }
    }

    dropDownMap['recent'] = true;
    dropDownMap['availability'] = true;

    setDropdownState(dropDownMap);
    setCategoryList(categoryList);
    setIsReady(true);
  };

  const restoreFiltersFromURL = () => {
    const { params } = parseHashParams();
    const filterParam = params.get('filter');

    if (filterParam) {
      const filterIds = filterParam.split(',');
      const restoredFilters = [];

      // Find filters by ID from the loaded categoryList
      filterIds.forEach((filterId) => {
        // Check availability filters first
        const availabilityFilter = availabilityFilters.find(
          (f) => f.id === filterId,
        );
        if (availabilityFilter) {
          restoredFilters.push(availabilityFilter);
          return;
        }

        // Then check category filters
        for (const category of categoryList) {
          const foundFilter = category.filterList.find(
            (filter) => filter.id === filterId,
          );
          if (foundFilter) {
            restoredFilters.push(foundFilter);
            break;
          }
        }
      });

      if (restoredFilters.length > 0) {
        onSelectedChange(restoredFilters);
      }
    }
  };

  const handleCheckboxChange = (value) => {
    const newSelected = [...selectedFilterList];
    const isExist = newSelected.some((select) => select.id === value.id);

    const updatedSelected = isExist
      ? newSelected.filter((select) => select.id !== value.id) // 제거
      : [...newSelected, value];

    onSelectedChange(updatedSelected);
  };

  const handleClearAllFilter = () => {
    onSelectedChange([]);
  };

  const toggleDropdown = (key) => {
    setDropdownState((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const filteredCategoryList = categoryList.filter(
    (category) =>
      category.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (category.codeNm &&
        category.codeNm.toLowerCase().includes(searchQuery.toLowerCase())) ||
      category.filterList.some(
        (item) =>
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.name &&
            item.name.toLowerCase().includes(searchQuery.toLowerCase())),
      ),
  );

  if (isReady) {
    return (
      <FilterContainer>
        {selectedFilterList.length > 0 && (
          <ClearButton
            onClick={() => {
              handleClearAllFilter();
            }}
          >
            Clear All
          </ClearButton>
        )}
        {selectedFilterList.length > 0 ? (
          <SelectedFilterGroup>
            {selectedFilterList.map((selected) => {
              return (
                <SelectedFilterItem key={selected.id}>
                  <CloseButton onClick={() => handleCheckboxChange(selected)}>
                    <IoCloseOutline size={'16px'} color={theme.colors.black} />
                  </CloseButton>
                  {selected.title}
                </SelectedFilterItem>
              );
            })}
          </SelectedFilterGroup>
        ) : null}
        {filteredCategoryList.length > 0 && (
          <SearchInput
            type='text'
            placeholder='Search for Category'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}

        {/* Availability Filter */}
        <FilterGroup>
          <GroupHeader onClick={() => toggleDropdown('availability')}>
            <Subtitle>AVAILABILITY</Subtitle>
            <DropdownArrow isOpen={dropdownState['availability']} />
          </GroupHeader>
          {dropdownState['availability'] &&
            availabilityFilters.map((filter) => {
              return (
                <FilterItem key={filter.id}>
                  <Label>
                    <Checkbox
                      label={filter.title}
                      checked={
                        selectedFilterList.filter((f) => f.id === filter.id)
                          .length > 0
                      }
                      onChange={() => handleCheckboxChange(filter)}
                    />
                  </Label>
                </FilterItem>
              );
            })}
        </FilterGroup>

        {filteredCategoryList.map((category) => {
          if (category.filterList.length > 0) {
            return (
              <FilterGroup key={category.id}>
                <GroupHeader onClick={() => toggleDropdown(category.id)}>
                  <Subtitle> {category.codeNm.toUpperCase()}</Subtitle>
                  <DropdownArrow isOpen={dropdownState[category.id]} />
                </GroupHeader>

                {dropdownState[category.id] &&
                  category.filterList
                    .filter(
                      (filter) => filter.title && filter.title.trim() !== '',
                    )
                    .filter(
                      (filter) =>
                        searchQuery === '' ||
                        filter.title
                          .toLowerCase()
                          .includes(searchQuery.toLowerCase()) ||
                        (filter.name &&
                          filter.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())),
                    )
                    .map((filter) => {
                      return (
                        <FilterItem key={filter.id}>
                          <Label>
                            <Checkbox
                              label={filter.title}
                              checked={
                                selectedFilterList.filter(
                                  (f) => f.title === filter.title,
                                ).length > 0
                              }
                              onChange={() => handleCheckboxChange(filter)}
                            />
                          </Label>
                        </FilterItem>
                      );
                    })}
              </FilterGroup>
            );
          }
        })}

        <FilterGroup>
          <GroupHeader onClick={() => toggleDropdown('recent')}>
            <Subtitle>{'RECENTLY VIEWED'}</Subtitle>
            <DropdownArrow isOpen={dropdownState['recent']} />
          </GroupHeader>
          {dropdownState['recent'] && (
            <>
              {recentViewList.length > 0 ? (
                recentViewList.map((item) => {
                  return <RecentProductCard key={item.idStyle} data={item} />;
                })
              ) : (
                <NoItemsMessage>No recently viewed items</NoItemsMessage>
              )}
            </>
          )}
        </FilterGroup>
      </FilterContainer>
    );
  }
}
